/**
 * App-side oto-güncelleme nöbetçisi.
 *
 * Sorun: kurulu PWA "kapat-aç"ta eski sayfayı/JS'i bellekte tutabiliyor;
 * yeni deploy kullanıcıya ulaşmıyor ("aynı gibi duruyor"). sw.js byte'ları
 * her deploy'da değişmediği için SW update döngüsü de her zaman tetiklenmez.
 *
 * Bu modül deploy-güdümlü, SW'den BAĞIMSIZ ikinci bir koruma katmanı:
 *   - Sayfa görünür olunca / pencere odaklanınca / periyodik (60sn) olarak
 *     `/asset-manifest.json`'ı cache:'no-store' ile çeker.
 *   - Çalışan sayfanın ana bundle hash'i ile karşılaştırır.
 *   - Hash değiştiyse (= yeni deploy) bir kez location.reload() yapar.
 *
 * Güvenlik:
 *   - Tek-atış guard (reloading) → reload döngüsü olmaz.
 *   - Sadece localhost değilse çalışır (dev'de HMR'ı bozmasın).
 *   - Ağ hatasında sessiz no-op.
 */

let started = false;
let reloading = false;
let runningHash: string | null = null;

/** Çalışan sayfanın <script src=".../static/js/main.<hash>.js"> hash'i. */
function readRunningMainHash(): string | null {
  try {
    const scripts = Array.from(document.getElementsByTagName("script"));
    for (const s of scripts) {
      const m = s.src && s.src.match(/\/static\/js\/main\.([a-z0-9]+)\.js/i);
      if (m) return m[1];
    }
  } catch (_) {}
  return null;
}

/** Sunucudaki güncel asset-manifest'ten ana bundle hash'i. */
async function fetchLatestMainHash(): Promise<string | null> {
  try {
    const res = await fetch("/asset-manifest.json", { cache: "no-store" });
    if (!res.ok) return null;
    const json = await res.json();
    const main: unknown = json?.files?.["main.js"] ?? json?.["main.js"];
    if (typeof main !== "string") return null;
    const m = main.match(/main\.([a-z0-9]+)\.js/i);
    return m ? m[1] : null;
  } catch (_) {
    return null;
  }
}

async function checkForUpdate(): Promise<void> {
  if (reloading) return;
  if (!runningHash) return; // hash okunamadıysa karşılaştırma yapma (yanlış reload riski)
  const latest = await fetchLatestMainHash();
  if (!latest) return;
  if (latest !== runningHash) {
    reloading = true;
    // App-shell cache'ini temizle ki kesinlikle taze shell gelsin (network-first
    // zaten taze çeker ama garanti olsun). MEDYA cache'lerini (`-media-`:
    // poster + m3u8) KORU — her deploy'da onları da silmek, ısıtılmış poster/
    // m3u8 katmanını yok edip deploy sonrası ilk scroll'da her şeyi yeniden
    // indirtiyordu (3M'de fleet-geneli soğuk-medya + thundering herd). Medya
    // cache adları sürümle değişmez (`mylasa-media-v1-*`), bayatlamaz.
    try {
      if (typeof caches !== "undefined") {
        const keys = await caches.keys();
        await Promise.all(
          keys
            .filter((k) => !/-media-/.test(k))
            .map((k) => caches.delete(k)),
        );
      }
    } catch (_) {}
    try {
      window.location.reload();
    } catch (_) {}
  }
}

export function startAutoUpdate(): void {
  if (started) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return; // dev: HMR'ı bozma
  started = true;

  runningHash = readRunningMainHash();

  // Görünür olunca + odaklanınca kontrol et (PWA'ya geri dönüş anı).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkForUpdate();
  });
  window.addEventListener("focus", () => void checkForUpdate());

  // Açık kalan oturumlar için periyodik kontrol (60sn).
  setInterval(() => {
    if (document.visibilityState === "visible") void checkForUpdate();
  }, 60_000);

  // NOT: SW kontrol değişiminde reload ETMİYORUZ — sw.js sürüm değişiminde
  // forced reload'u zaten SW'nin activate handler'ı (client.navigate) yapıyor.
  // Buradaki asset-manifest kontrolü ise sw.js DEĞİŞMEDEN yapılan app-only
  // deploy'ları yakalar. İki katman çakışmaz.

  // İlk açılışta da bir kez kontrol (stale shell ile açıldıysak hızla düzelt).
  void checkForUpdate();
}

export default { startAutoUpdate };
